import type { ReactElement, ReactNode } from 'react';
import { useInView } from 'react-intersection-observer';

import { IconCircleTickFilled } from '@xing-com/icons';
import { List } from '@xing-com/list';
import { scale110 } from '@xing-com/tokens';
import { BodyCopy, Headline, Hero, Meta } from '@xing-com/typography';

import { NewPerkMarker } from '../perk-list/new-perk-marker';
import type { ProductPerks } from '../perk-list/perk-list.types';
import { CardMembershipStatus } from '../product-cards/components/sub-components/card-membership-status';

import * as Styled from './perk-list-mobile.styled';

export type PerkListMobileProps = {
  action: string;
  activationUrl?: string;
  cancellation: string;
  owned?: boolean;
  isShown: boolean;
  onActivate?: () => void;
  onClose: () => void;
  perks: ProductPerks;
  previousPrice: ReactNode;
  price: ReactNode;
  membershipStatusMessage?: string;
  title: string;
};

type ActionGroupContentProps = Pick<
  PerkListMobileProps,
  'action' | 'activationUrl' | 'cancellation'
> &
  Pick<PerkListMobileProps, 'onActivate'>;

const ActionGroupContent = ({
  action,
  activationUrl,
  cancellation,
  onActivate,
}: ActionGroupContentProps): ReactElement => (
  <>
    <Styled.ActivationButton
      href={activationUrl}
      onClick={onActivate}
      size="large"
      variant="primary"
    >
      {action}
    </Styled.ActivationButton>
    <Styled.Cancellation>
      <Meta size="xsmall" lineHeight="dense" noMargin>
        {cancellation}
      </Meta>
    </Styled.Cancellation>
  </>
);

export const PerkListMobile = ({
  action,
  activationUrl,
  cancellation,
  owned,
  isShown,
  onActivate,
  onClose,
  perks,
  previousPrice,
  price,
  membershipStatusMessage,
  title,
}: PerkListMobileProps): ReactElement => {
  const isOwned = owned === true;
  const [actionGroupRef, isActionGroupInView] = useInView({
    fallbackInView: true,
    initialInView: true,
  });

  return (
    <>
      <Styled.ModalPage
        closeButton={{ onClick: onClose }}
        focusTrapProps={{ active: false }}
        isAnimated
        isShown={isShown}
        noGrid
        noMargin
      >
        <Styled.Content>
          <Styled.ProductHeader>
            <Styled.ProductSummary>
              <Hero as="h2" size="medium" noMargin>
                {title}
              </Hero>
              {isOwned ? (
                <CardMembershipStatus>
                  {membershipStatusMessage ?? ''}
                </CardMembershipStatus>
              ) : (
                <Styled.Pricing>
                  <BodyCopy size="xlarge" noMargin>
                    {price}
                  </BodyCopy>
                  <BodyCopy size="small" lineHeight="dense" noMargin>
                    {previousPrice}
                  </BodyCopy>
                </Styled.Pricing>
              )}
            </Styled.ProductSummary>
            <BodyCopy size="large" noMargin>
              {perks.intro}
            </BodyCopy>
            {isOwned ? null : (
              <Styled.ActionGroup ref={actionGroupRef}>
                <ActionGroupContent
                  action={action}
                  activationUrl={activationUrl}
                  cancellation={cancellation}
                  onActivate={onActivate}
                />
              </Styled.ActionGroup>
            )}
          </Styled.ProductHeader>

          <Styled.Groups data-qa="perk-list-mobile">
            {perks.groups.map((group) => (
              <Styled.Group key={group.heading}>
                <Headline as="h3" noMargin size="xlarge">
                  {group.heading}
                </Headline>
                <List>
                  {group.perks.map((perk) => (
                    <Styled.Perk key={perk.text}>
                      <IconCircleTickFilled
                        aria-hidden="true"
                        height={scale110}
                        width={scale110}
                      />
                      <Styled.PerkText>
                        <BodyCopy as="span" noMargin size="medium">
                          {perk.text}
                        </BodyCopy>
                      </Styled.PerkText>
                      {perk.isNew ? <NewPerkMarker /> : null}
                    </Styled.Perk>
                  ))}
                </List>
              </Styled.Group>
            ))}
          </Styled.Groups>
        </Styled.Content>
      </Styled.ModalPage>
      {isOwned ? null : (
        <Styled.ActionBanner
          display="fixed"
          show={isShown && !isActionGroupInView}
          showCloseButton={false}
          variant="soft"
        >
          <Styled.ActionGroup>
            <ActionGroupContent
              action={action}
              activationUrl={activationUrl}
              cancellation={cancellation}
              onActivate={onActivate}
            />
          </Styled.ActionGroup>
        </Styled.ActionBanner>
      )}
    </>
  );
};
